/**
 * FLO-325: a stable, per-browser anonymous visitor id for the website-agent.
 *
 * The website agent engages a visitor with zero identity ask, but the session
 * must still be attributable from turn 1. This id — a client-generated UUID
 * persisted in localStorage — is the stable anonymous key: repeat visits map to
 * the SAME anonymous EndUser, and when the visitor later gives an email it is
 * merged into the emailed EndUser (server side: resolveOrCreate anonymous branch
 * + mergeAnonymousEndUser). Forwarded as userInfo.anonymousId on start-session.
 *
 * Consent/PII note (FLO-185): this id + any later-captured email fall under the
 * existing encryption/retention purge.
 */
const STORAGE_KEY = 'floe_anonymous_id';

/** In-memory fallback so the id is stable within a page load when localStorage
 *  is unavailable (private mode / SSR). */
let fallbackId: string | null = null;

/** A fresh anonymous id. Prefers crypto.randomUUID; degrades gracefully. */
export function generateAnonymousId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `anon_${crypto.randomUUID()}`;
    }
  } catch {
    /* crypto unavailable */
  }
  return `anon_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Get the visitor's stable anonymous id, creating + persisting one on first use.
 * Never throws — falls back to an in-memory id if localStorage is blocked.
 */
export function getOrCreateAnonymousId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const id = generateAnonymousId();
    localStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    if (!fallbackId) fallbackId = generateAnonymousId();
    return fallbackId;
  }
}

/** Attach the browser-owned identity without discarding host-supplied profile fields. */
export function withAnonymousId<T extends object>(userInfo?: T | null): T & { anonymousId: string } {
  return {
    ...(userInfo ?? ({} as T)),
    anonymousId: getOrCreateAnonymousId(),
  };
}

/** Build the hidden resolution control; dismiss carries no identity payload. */
export function identityResolutionControlPayload<T extends object>(userInfo?: T | null):
  | Record<string, never>
  | { userInfo: T & { anonymousId: string } } {
  return userInfo ? { userInfo: withAnonymousId(userInfo) } : {};
}
